// Hooks do Analytics do site (GA4).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AnalyticsCampaignsDto, AnalyticsPropertiesResponse, AnalyticsSourceDto, AnalyticsSummaryDto } from '@impulso/shared/api/analytics'
import { api } from '../../lib/api'

const key = (companyId: string) => ['analytics', companyId] as const

export function useAnalyticsSummary(companyId: string, days: number) {
  return useQuery({
    queryKey: [...key(companyId), 'summary', days],
    queryFn: () => api.get<AnalyticsSummaryDto>(`/companies/${companyId}/analytics/summary?days=${days}`),
    // enquanto importa o histórico, atualiza sozinho
    refetchInterval: (q) => (q.state.data?.source?.backfilling ? 10_000 : false),
  })
}

export function useAnalyticsCampaigns(companyId: string, days: number) {
  return useQuery({
    queryKey: [...key(companyId), 'campaigns', days],
    queryFn: () => api.get<AnalyticsCampaignsDto>(`/companies/${companyId}/analytics/campaigns?days=${days}`),
  })
}

export function useAnalyticsProperties(companyId: string, credentialId: string | null) {
  return useQuery({
    queryKey: [...key(companyId), 'properties', credentialId],
    queryFn: () => api.get<AnalyticsPropertiesResponse>(`/companies/${companyId}/analytics/properties?credentialId=${credentialId}`),
    enabled: !!credentialId,
    retry: false,
  })
}

/** Inicia o OAuth e leva o navegador ao Google. */
export function useConnectAnalytics(companyId: string) {
  return useMutation({
    mutationFn: () => api.post<{ authUrl: string }>(`/companies/${companyId}/analytics/connect`),
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
  })
}

export function useAnalyticsAction(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { kind: 'select'; credentialId: string; propertyId: string } | { kind: 'sync' | 'disconnect' }): Promise<unknown> =>
      a.kind === 'select' ? api.post<AnalyticsSourceDto>(`/companies/${companyId}/analytics/source`, { credentialId: a.credentialId, propertyId: a.propertyId })
        : a.kind === 'sync' ? api.post<{ ok: true }>(`/companies/${companyId}/analytics/sync`)
          : api.del<void>(`/companies/${companyId}/analytics/source`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  })
}
